import { useState, useEffect, useMemo, useCallback } from 'react';
import { NeedsAssessmentModal } from './NeedsAssessmentModal';
import {
  getCustomCalibres,
  getCustomCalibreById,
  getCustomCalibresByIds,
  deleteCustomCalibre,
  bulkSetCalibreManufacturer,
  bulkSetCalibreBrand,
  bulkSetCalibreAliasBrands,
  bulkSetCalibreImageSource,
  bulkSetCalibreSource,
  backfillCalibreTiers,
  BACKFILL_TIERS_MAX_IDS,
  backfillDeprecatedCalibreHygiene,
  getWatchBrandsByIds,
  CALIBRE_ENTITY_SOURCES,
  CALIBRE_IMAGE_SOURCES,
  type CustomCalibreEntry,
  type CalibreImage,
  type CalibreEntitySource,
  type CalibreImageSource,
  type ManufacturerCatalogueEntry,
} from '../../services/firebase';
import { fetchCalibreListing, isAlgoliaConfigured } from '../../services/algolia';
import {
  NO_MANUFACTURER_FILTER,
  buildManufacturerFilterOptions,
  calibreDocToRow,
  calibreHitToRow,
  calibreMatchesManufacturerFilter,
  defaultShowAll,
  originalMakerBadge,
  calibreRowBrandKey,
  describeCalibreRefs,
  filterCalibresByDeprecated,
  toggleRowsInSelection,
  type CalibreListRow,
} from '../../services/calibreListQuery';
import { localCalibreMatches } from '../../services/calibreSearchQuery';
import { useCalibreListSearch } from '../../hooks/useCalibreListSearch';
import { useRelevanceSort } from '../../hooks/useRelevanceSort';
import { useResolvedHitImage } from '../../hooks/useResolvedHitImage';
import { CALIBRE_SOURCE_LABELS } from '../../services/calibreSourceLabels';
import { useManufacturerCatalogue } from '../../hooks/useManufacturerCatalogue';
import { RELEVANCE_SORT, type WithRelevance } from '../../services/relevanceSort';
import { RelevanceSortToggle } from '../common/RelevanceSortToggle';
import { SortIcon } from '../common/SortIcon';
import {
  calibreManufacturerIds,
  classifyCalibreGeneric,
  defaultAkaBrandId,
  type CalibreManufacturerRefs,
} from '../../services/calibreClassification';
import {
  planCalibreAkaBrandUpdates,
  planCalibreBrandUpdates,
  type CalibreAkaBrandPlan,
  type CalibreBrandPlan,
} from '../../services/calibreBrandBulk';
import { normalizeCalibreKey } from '../../services/calibreResolution';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { Badge } from '../common/Badge';
import { CustomCalibreModal } from '../CuratedWatches/CustomCalibreModal';
import { BulkCalibreAIEnhance } from './BulkCalibreAIEnhance';
import { CalibreBulkActionBar } from './CalibreBulkActionBar';
import toast from 'react-hot-toast';

/**
 * The Image column.
 *
 * The indexed `imageUrl` CANNOT be rendered directly, which is what this used to
 * do. The indexer writes a tokenless `…/o/calibres%2F…?alt=media` URL, and an
 * `<img>` tag cannot send a Firebase auth header, so the browser fetches it
 * anonymously and Storage rules answer 403 — every calibre thumbnail a broken
 * icon. Every one of the 175 indexed calibre images was affected, going back to
 * the first of them; it only became conspicuous once enough calibres had
 * pictures for the column to be mostly full rather than mostly dashes.
 *
 * Reference thumbnails carry the same tokenless URLs and render fine, which is
 * what made this look like a calibre-specific data problem. It isn't: their
 * bytes live under `brands/…`, which IS publicly readable, while `calibres/…`
 * is not. So the round trip below is owed on calibres and genuinely not owed on
 * references — a rules difference, not an inconsistency between the two screens.
 *
 * `useResolvedHitImage` is the same helper the reference surfaces already use.
 * It re-signs through `getDownloadURL` ONLY when the URL lacks a token, so if
 * the rules or the indexer ever change, this quietly stops costing anything.
 *
 * One input covers all three shapes the row can carry: the indexed absolute URL,
 * a bare `storagePath` from the Firestore fallback, and a legacy absolute
 * `image.url` (left alone when it points somewhere other than Storage).
 */
function CalibreThumbnail({
  imageUrl,
  image,
}: {
  imageUrl?: string;
  image?: CalibreImage;
}) {
  const resolvedUrl = useResolvedHitImage(
    imageUrl || image?.storagePath || image?.url || undefined,
  );

  if (!imageUrl && !image) return <span className='text-gray-300'>—</span>;
  if (!resolvedUrl) {
    return <div className='w-10 h-10 rounded bg-gray-100 mx-auto' />;
  }
  return (
    <img
      src={resolvedUrl}
      alt=''
      className='w-10 h-10 rounded object-cover mx-auto border border-gray-200'
      onError={(e) => { (e.target as HTMLImageElement).style.opacity = '0.3'; }}
    />
  );
}

type SortField = 'name' | 'brand' | 'manufacturer' | 'hasImage' | 'refs' | 'createdAt';
type SortDir = 'asc' | 'desc';

/** The sort the listing opens on, and the one relevance hands back to. */
const DEFAULT_SORT_FIELD: SortField = 'name';
type GenericFilter = 'all' | 'generic' | 'non-generic';

const PAGE_SIZE_OPTIONS = [25, 50, 100];


/**
 * The search box's offline fallback.
 *
 * Exactly what the box matched before Algolia — calibre name, every alias, and
 * the lenient brand key — so a degraded search never matches LESS than it used
 * to. `normalizeCalibreKey` on both sides is what keeps "caliber" and "calibre"
 * interchangeable. Module scope so it is referentially stable across renders
 * (the hook memoizes on it).
 */
function listRowMatches(row: CalibreListRow, term: string): boolean {
  if (localCalibreMatches(row, term)) return true;
  const needle = normalizeCalibreKey(term);
  if (!needle) return true;
  return calibreRowBrandKey(row).toLowerCase().includes(needle);
}

/**
 * What a scoped "Backfill AI tiers" dry run found, folded across its chunks.
 *
 * Held as state rather than derived, because it is the answer to a specific
 * (ids, force) pair: it must not drift out from under the Run button when the
 * selection or the force toggle changes — the modal re-previews instead.
 */
interface TiersBackfillPreview {
  /** The ids this preview was built from. */
  ids: string[];
  /** The force setting it was built under. */
  force: boolean;
  /** Calibres this run would enrich. */
  candidates: number;
  /** Calibres left alone because they already have tiers (force off). */
  skippedWithTiers: number;
  /** Selected ids no longer in `custom_calibres`. */
  notFound: number;
  /** Of the candidates, how many already have tiers and would be OVERWRITTEN. */
  overwrites: number;
  /** Of the candidates, how many an earlier backfill run already attempted. */
  attemptedBefore: number;
  /** Candidate names, for the preview list. */
  candidateNames: string[];
  /** The names `force` would overwrite — the ones the confirm is about. */
  overwriteNames: string[];
}

/** Split ids into chunks the callable will accept in one invocation. */
function chunkIds(ids: string[], size: number): string[][] {
  const out: string[][] = [];
  for (let i = 0; i < ids.length; i += size) out.push(ids.slice(i, i + size));
  return out;
}

function StatPill({ label, value, accent }: { label: string; value: number | string; accent?: 'blue' | 'emerald' | 'amber' }) {
  const accentClasses =
    accent === 'blue' ? 'bg-blue-50 text-blue-700' :
    accent === 'emerald' ? 'bg-emerald-50 text-emerald-700' :
    accent === 'amber' ? 'bg-amber-50 text-amber-700' :
    'bg-gray-100 text-gray-700';
  return (
    <div className={`px-3 py-1.5 rounded-lg text-sm ${accentClasses}`}>
      <span className='font-semibold'>{value}</span>
      <span className='text-xs ml-1.5 opacity-80'>{label}</span>
    </div>
  );
}

interface CuratedCalibresProps {
  /** Deep-link target: once the list loads, open this calibre's editor modal. */
  openCalibreId?: string;
  /** Called after the deep-link target has been opened (so the parent can clear it). */
  onOpenCalibreConsumed?: () => void;
}

export function CuratedCalibres({ openCalibreId, onOpenCalibreConsumed }: CuratedCalibresProps = {}) {
  /**
   * The listing, as ROWS rather than documents.
   *
   * A row is an Algolia projection: no `images` array, no `data` beyond four
   * specs, no `aliasDetails`. Nothing here is ever written back — every action
   * that touches an unindexed field hydrates the row to a real document first
   * (see `openEditor`, `hydrateSelection`).
   */
  const [rows, setRows] = useState<CalibreListRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  /** True when the rows came from the Firestore fallback, not the index. */
  const [degraded, setDegraded] = useState(false);
  /** A document read is in flight for an action that needs the full calibre. */
  const [hydrating, setHydrating] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [groupBy, setGroupBy] = useState<string>(''); // '' = no grouping; 'brand' = group by brandName
  // Widened to admit `'relevance'`, which is not a column — see
  // `services/relevanceSort.ts`. The comparator and the header arrows read
  // `effectiveSortField` instead, so the sentinel never reaches them.
  const [sortField, setSortField] = useState<WithRelevance<SortField>>(DEFAULT_SORT_FIELD);
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [genericFilter, setGenericFilter] = useState<GenericFilter>('all');
  // '' = all; NO_MANUFACTURER_FILTER = none set; otherwise a manufacturer id.
  const [manufacturerFilter, setManufacturerFilter] = useState<string>('');
  // "Show all calibres associated with this manufacturer". null = the maker's
  // default (defaultShowAll: strict for a current maker, all for a
  // historical-only one); reset whenever the manufacturer changes.
  const [showAllChoice, setShowAllChoice] = useState<boolean | null>(null);
  const [showDeprecated, setShowDeprecated] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalEntry, setModalEntry] = useState<CustomCalibreEntry | null>(null);
  const [modalCreate, setModalCreate] = useState(false);

  // Multi-select state
  const [selected, setSelected] = useState<Set<string>>(new Set());
  /**
   * The selection as full Firestore documents, loaded when a tool that needs
   * them is opened. `bulkSetCalibreImageSource` REWRITES the `images` array and
   * the AI tool writes back arbitrary `data.*` fields — neither is on a listing
   * row, so handing either a projection would blank real values.
   */
  const [selectionDocs, setSelectionDocs] = useState<CustomCalibreEntry[]>([]);

  // Bulk Set Manufacturer modal state
  const [bulkSetMfrOpen, setBulkSetMfrOpen] = useState(false);
  const [bulkTargetMfrId, setBulkTargetMfrId] = useState('');
  const [bulkRunning, setBulkRunning] = useState(false);

  // Bulk Set Brand / Set AKA Brands modal state.
  //
  // Both hold a PLAN rather than recomputing from `rows`/`selectionDocs` while
  // open: the plan is built from an async brand resolution (see
  // `resolveBrandTargets`), so a `useMemo` over state would render one frame of
  // wrong counts before the fetch lands — and the AKA plan is built from the
  // documents `hydrateSelection` RETURNS, which `selectionDocs` doesn't yet hold
  // at that point.
  const [bulkSetBrandOpen, setBulkSetBrandOpen] = useState(false);
  const [brandPlan, setBrandPlan] = useState<CalibreBrandPlan | null>(null);
  const [bulkAkaBrandsOpen, setBulkAkaBrandsOpen] = useState(false);
  const [akaPlan, setAkaPlan] = useState<CalibreAkaBrandPlan | null>(null);
  /** Display names for the brands the current plan targets. Id → name. */
  const [brandNameById, setBrandNameById] = useState<Map<string, string>>(new Map());

  // Bulk Set Image Source modal state
  const [bulkImgSrcOpen, setBulkImgSrcOpen] = useState(false);
  const [bulkTargetImgSrc, setBulkTargetImgSrc] = useState<CalibreImageSource | ''>('');

  // Bulk Set Calibre Source modal state (entity-level `source`, not per-image)
  const [bulkCalSrcOpen, setBulkCalSrcOpen] = useState(false);
  const [bulkTargetCalSrc, setBulkTargetCalSrc] = useState<CalibreEntitySource | ''>('');

  // Bulk AI Calibre Enhancement modal state
  const [aiEnhanceOpen, setAiEnhanceOpen] = useState(false);

  // "Backfill AI tiers" admin action — populate data.tiers by re-running the
  // enrich core per calibre. Two scopes, chosen by context: the toolbar button
  // runs it over the SELECTION, and the page button (shown only when nothing is
  // selected) runs the original whole-collection sweep.
  const [tiersBackfillOpen, setTiersBackfillOpen] = useState(false);
  const [tiersBackfillRunning, setTiersBackfillRunning] = useState(false);
  const [tiersBackfillDryRun, setTiersBackfillDryRun] = useState(true);
  const [tiersBackfillLog, setTiersBackfillLog] = useState<string[]>([]);
  const [tiersBackfillScope, setTiersBackfillScope] = useState<'all' | 'selection'>('all');
  /**
   * The ids the scoped run works on, SNAPSHOT at open time. The selection can be
   * cleared (or the listing refreshed) while a multi-batch run is in flight, and
   * a run that silently changed target halfway would be untraceable.
   */
  const [tiersBackfillIds, setTiersBackfillIds] = useState<string[]>([]);
  /**
   * Re-enrich calibres that already have tiers, OVERWRITING them. Scoped runs
   * only, off by default: bulk actions here are fill-empty as a house rule, and
   * this is the one deliberate exception — fenced behind a preview that names
   * every calibre it would overwrite and a confirm on Run.
   */
  const [tiersBackfillForce, setTiersBackfillForce] = useState(false);
  const [tiersBackfillPreview, setTiersBackfillPreview] =
    useState<TiersBackfillPreview | null>(null);
  const [tiersBackfillPreviewing, setTiersBackfillPreviewing] = useState(false);
  const [tiersBackfillPreviewError, setTiersBackfillPreviewError] = useState<string | null>(null);

  // "Repair deprecated calibres" admin action — moves aliases still stranded on
  // soft-deleted calibres onto their redirect target, then strips and renames
  // the source. Repairs the calibres reclassified before the wizard moved a
  // whole alias set; see functions/src/deprecatedCalibreHygiene.ts.
  const [hygieneOpen, setHygieneOpen] = useState(false);
  const [needsAssessmentOpen, setNeedsAssessmentOpen] = useState(false);
  const [hygieneRunning, setHygieneRunning] = useState(false);
  const [hygieneDryRun, setHygieneDryRun] = useState(true);
  const [hygieneLog, setHygieneLog] = useState<string[]>([]);

  /**
   * The listing itself, from Algolia — two search requests in place of the
   * ~1,822-document `getCustomCalibres()` this page used to run on every mount,
   * plus one Storage round trip per visible thumbnail.
   *
   * Deprecated calibres are fetched too and split off LOCALLY, so the "only
   * deprecated" toggle costs no round trip. Everything else — search matching
   * aside — stays local for the same reason: the page holds every record.
   *
   * The Firestore collection read survives as the FALLBACK. Algolia being
   * unconfigured or down must degrade this page to slow-and-complete, never to
   * empty, which is the same choice the brand and reference listings made.
   */
  const loadRows = useCallback(async (fromServer = false) => {
    if (isAlgoliaConfigured()) {
      try {
        const hits = await fetchCalibreListing();
        setRows(hits.map(calibreHitToRow));
        setDegraded(false);
        return;
      } catch (error) {
        console.warn(
          'Calibre listing unavailable from Algolia, falling back to Firestore',
          error,
        );
      }
    }
    const docs = await getCustomCalibres(fromServer, true);
    setRows(docs.map(calibreDocToRow));
    setDegraded(true);
  }, []);

  const loadEntries = useCallback(async () => {
    setLoading(true);
    try {
      await loadRows();
    } catch (error) {
      console.error('Failed to load calibres:', error);
      toast.error('Failed to load calibres');
    } finally {
      setLoading(false);
    }
  }, [loadRows]);

  // Re-fetch the list (same query as the initial load) while keeping the
  // current list on screen — only the button shows a spinner. Both paths force a
  // fresh read: the Algolia calls pass `cacheable: false`, and the Firestore
  // fallback reads from the server, so edits made elsewhere (another tab, a
  // backfill) show up rather than replaying a cached snapshot.
  // Sort / filter / search / pagination / selection state is untouched — only
  // the underlying rows are swapped.
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await loadRows(true);
      toast.success('Calibres refreshed');
    } catch (error) {
      console.error('Failed to refresh calibres:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to refresh calibres');
    } finally {
      setRefreshing(false);
    }
  };

  /**
   * Overwrite one row with its authoritative Firestore document.
   *
   * Called after a save, because the Algolia record is written by a trigger and
   * lags the document by a moment — reloading the listing alone can therefore
   * replay the pre-edit values for the very calibre the admin just changed. A
   * row rebuilt from the document can never be stale.
   */
  const refreshRowFromDoc = useCallback(async (id: string) => {
    try {
      const doc = await getCustomCalibreById(id);
      if (!doc) {
        setRows((prev) => prev.filter((r) => r.id !== id));
        return;
      }
      const row = calibreDocToRow(doc);
      setRows((prev) =>
        prev.some((r) => r.id === id)
          ? prev.map((r) => (r.id === id ? row : r))
          : [...prev, row],
      );
    } catch (error) {
      // Non-fatal: the listing reload has already run, so the row is present —
      // just possibly one indexer-tick behind.
      console.warn('Could not refresh calibre row from Firestore', error);
    }
  }, []);

  const handleSaved = useCallback(
    async (savedId: string) => {
      await loadRows();
      await refreshRowFromDoc(savedId);
    },
    [loadRows, refreshRowFromDoc],
  );

  /**
   * Apply values we KNOW were written to the rows they were written to.
   *
   * Run after a bulk write completes and the listing has reloaded: the reload
   * may still be serving the pre-write index records, and re-showing the old
   * manufacturer on a row the admin just set one on reads as a failed write.
   */
  const patchRows = useCallback(
    (ids: string[], patch: Partial<CalibreListRow>) => {
      const idSet = new Set(ids);
      setRows((prev) =>
        prev.map((r) => (idSet.has(r.id) ? { ...r, ...patch } : r)),
      );
    },
    [],
  );

  // Run the deprecated-calibre hygiene sweep. Idempotent and dry-run by
  // default; a capped run just means running it again, so the real path loops
  // while the callable reports it stopped early.
  const runHygiene = async () => {
    setHygieneRunning(true);
    setHygieneLog([]);
    const log = (line: string) => setHygieneLog((prev) => [...prev, line]);
    try {
      let pass = 0;
      let totalRepaired = 0;
      let totalAkas = 0;
      let totalRenamed = 0;
      // Belt-and-braces cap so a stoppedEarly that never clears can't loop on.
      const MAX_PASSES = 20;
      for (;;) {
        pass += 1;
        const r = await backfillDeprecatedCalibreHygiene({ dryRun: hygieneDryRun });
        totalRepaired += r.repaired;
        totalAkas += r.akasTransferred;
        totalRenamed += r.sourcesRenamed;
        log(
          `${hygieneDryRun ? 'Dry run' : `Pass ${pass}`} — scanned ${r.scanned} deprecated calibre(s); ` +
            `${r.repaired} to repair, ${r.akasTransferred} alias(es) to move, ` +
            `${r.sourcesStripped} to strip, ${r.sourcesRenamed} to rename.`,
        );
        r.samples.slice(0, 15).forEach((sample) => {
          const moved = sample.transferred.length > 0 ? sample.transferred.join(', ') : 'none';
          log(`  • ${sample.calibreName || sample.calibreId} → ${sample.targetCalibreId}: ${moved}`);
        });
        if (r.errors.length > 0) log(`  ${r.errors.length} error(s): ${r.errors[0].error}`);
        // A dry run reports the whole picture in one pass; only a real run loops.
        if (hygieneDryRun || !r.stoppedEarly || pass >= MAX_PASSES) {
          if (r.stoppedEarly && !hygieneDryRun) {
            log('Stopped early and the pass cap was hit — run again to continue.');
          }
          break;
        }
      }
      log(
        hygieneDryRun
          ? totalRepaired === 0
            ? 'Nothing to repair — every deprecated calibre is already clean.'
            : 'Turn off “Dry run” and press Run to apply.'
          : `Done — repaired ${totalRepaired} calibre(s), moved ${totalAkas} alias(es), renamed ${totalRenamed}.`,
      );
      if (!hygieneDryRun) await loadRows();
    } catch (error) {
      log(`Failed: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setHygieneRunning(false);
    }
  };

  /**
   * Build the scoped preview: a chunked DRY RUN over the selected ids.
   *
   * Server-side rather than from the listing rows, because none of the three
   * inputs is indexed — `data.tiers` isn't on an Algolia row at all, and neither
   * is the backfill marker. The dry run makes no AI calls and no writes; it costs
   * one document read per selected calibre.
   */
  const loadTiersBackfillPreview = useCallback(async (ids: string[], force: boolean) => {
    if (ids.length === 0) {
      setTiersBackfillPreview(null);
      return;
    }
    setTiersBackfillPreviewing(true);
    setTiersBackfillPreviewError(null);
    try {
      const preview: TiersBackfillPreview = {
        ids,
        force,
        candidates: 0,
        skippedWithTiers: 0,
        notFound: 0,
        overwrites: 0,
        attemptedBefore: 0,
        candidateNames: [],
        overwriteNames: [],
      };
      for (const chunk of chunkIds(ids, BACKFILL_TIERS_MAX_IDS)) {
        const r = await backfillCalibreTiers({ dryRun: true, calibreIds: chunk, force });
        preview.candidates += r.candidates;
        preview.skippedWithTiers += r.skippedWithTiers;
        for (const entry of r.results) {
          if (entry.status === 'not-found') preview.notFound += 1;
          if (entry.status !== 'would-enrich') continue;
          if (entry.overwrote) {
            preview.overwrites += 1;
            preview.overwriteNames.push(entry.calibreName);
          }
          if (entry.attemptedBefore) preview.attemptedBefore += 1;
          preview.candidateNames.push(entry.calibreName);
        }
      }
      setTiersBackfillPreview(preview);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not preview the backfill';
      setTiersBackfillPreview(null);
      setTiersBackfillPreviewError(msg);
    } finally {
      setTiersBackfillPreviewing(false);
    }
  }, []);

  /**
   * Open the backfill over the SELECTION. The ids are snapshotted here so the
   * run keeps its target even if the selection is cleared while it works.
   */
  const openTiersBackfillForSelection = () => {
    const ids = [...selected];
    setTiersBackfillScope('selection');
    setTiersBackfillIds(ids);
    setTiersBackfillForce(false);
    setTiersBackfillLog([]);
    setTiersBackfillPreview(null);
    setTiersBackfillPreviewError(null);
    setTiersBackfillOpen(true);
    void loadTiersBackfillPreview(ids, false);
  };

  /** Open the original whole-collection sweep (page button, nothing selected). */
  const openTiersBackfillForAll = () => {
    setTiersBackfillScope('all');
    setTiersBackfillIds([]);
    setTiersBackfillForce(false);
    setTiersBackfillDryRun(true);
    setTiersBackfillLog([]);
    setTiersBackfillPreview(null);
    setTiersBackfillPreviewError(null);
    setTiersBackfillOpen(true);
  };

  /** Force changes what the preview means, so the preview is rebuilt with it. */
  const toggleTiersBackfillForce = (force: boolean) => {
    setTiersBackfillForce(force);
    void loadTiersBackfillPreview(tiersBackfillIds, force);
  };

  /**
   * Run the backfill over the SELECTION.
   *
   * Sends the ids in slices the size of one invocation's AI budget, draining the
   * `remainingIds` cursor each slice returns — there is no marker to converge on
   * when forcing, so the ids not reached come back explicitly rather than being
   * re-derived. Auto-accepts AI tiers, exactly as the sweep does.
   */
  const runScopedTiersBackfill = async () => {
    const ids = tiersBackfillIds;
    if (ids.length === 0) return;
    const overwrites = tiersBackfillForce ? tiersBackfillPreview?.overwrites ?? 0 : 0;
    if (overwrites > 0) {
      const names = (tiersBackfillPreview?.overwriteNames ?? []).slice(0, 10).join(', ');
      const ok = confirm(
        `Force is on: ${overwrites} calibre${overwrites === 1 ? '' : 's'} already ` +
          `${overwrites === 1 ? 'has' : 'have'} tiers and will be OVERWRITTEN with whatever ` +
          `the AI returns.\n\n${names}${overwrites > 10 ? ', …' : ''}\n\nContinue?`,
      );
      if (!ok) return;
    }

    setTiersBackfillRunning(true);
    setTiersBackfillLog([]);
    const log = (line: string) => setTiersBackfillLog((prev) => [...prev, line]);
    try {
      let batch = 0;
      let totalProcessed = 0;
      let totalUpdated = 0;
      let totalTiers = 0;
      let totalErrors = 0;
      let totalSkipped = 0;
      // Belt-and-braces cap so a misbehaving cursor can never loop forever.
      const MAX_BATCHES = 500;
      // One slice per invocation's AI budget, so a call never re-reads hundreds
      // of documents it has no budget to process.
      for (const chunk of chunkIds(ids, 20)) {
        if (batch >= MAX_BATCHES) {
          log('Stopped at the batch cap — run again to continue.');
          break;
        }
        let pending = chunk;
        while (pending.length > 0 && batch < MAX_BATCHES) {
          batch += 1;
          const r = await backfillCalibreTiers({
            dryRun: false,
            calibreIds: pending,
            force: tiersBackfillForce,
          });
          totalProcessed += r.processed;
          totalUpdated += r.calibresUpdated;
          totalTiers += r.tiersAdded;
          totalErrors += r.errors.length;
          totalSkipped += r.skippedWithTiers;
          log(
            `Batch ${batch}: enriched ${r.processed}, added ${r.tiersAdded} tier(s) on ${r.calibresUpdated} calibre(s)` +
              (r.skippedWithTiers ? `, ${r.skippedWithTiers} skipped (already have tiers)` : '') +
              (r.errors.length ? `, ${r.errors.length} error(s)` : '') +
              (r.remainingIds.length ? ` — ${r.remainingIds.length} still to do…` : '.'),
          );
          r.results
            .filter((e) => e.status === 'enriched')
            .slice(0, 8)
            .forEach((e) => log(`  • ${e.calibreName}: ${e.tierNames.join(', ')}`));
          r.results
            .filter((e) => e.status === 'error')
            .slice(0, 5)
            .forEach((e) => log(`  ✕ ${e.calibreName}: ${e.error ?? 'failed'}`));
          // No progress with work still pending means the server can't move on
          // this slice; stop rather than re-sending the same ids forever.
          if (r.processed === 0 && r.remainingIds.length > 0) {
            log('  Stopped: the last batch made no progress.');
            pending = [];
            break;
          }
          pending = r.remainingIds;
        }
      }
      log(
        `Done — enriched ${totalProcessed} calibre(s); added ${totalTiers} tier(s) across ${totalUpdated} calibre(s)` +
          (totalSkipped ? `; ${totalSkipped} skipped (already have tiers)` : '') +
          (totalErrors ? `; ${totalErrors} error(s)` : '') + '.',
      );
      toast.success(`Backfill complete: +${totalTiers} tier(s) on ${totalUpdated} calibre(s)`);
      await handleRefresh();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Backfill failed';
      log(`Error: ${msg}`);
      toast.error(msg);
    } finally {
      setTiersBackfillRunning(false);
    }
  };

  // Run the AI tier backfill. Dry run = one cheap scan reporting candidate count.
  // Real run = loop the callable (each invocation enriches up to `limit` calibres,
  // returns hasMore) until the whole eligible set is processed, streaming a
  // per-batch progress log. Auto-accepts AI tiers; fill-empty only.
  const runTiersBackfill = async () => {
    if (tiersBackfillScope === 'selection') {
      await runScopedTiersBackfill();
      return;
    }
    setTiersBackfillRunning(true);
    setTiersBackfillLog([]);
    const log = (line: string) => setTiersBackfillLog((prev) => [...prev, line]);
    try {
      if (tiersBackfillDryRun) {
        const r = await backfillCalibreTiers({ dryRun: true });
        log(
          `Dry run — scanned ${r.scanned} calibre(s); ${r.candidates} eligible for tier backfill (empty tiers, not yet attempted).`,
        );
        r.samples.slice(0, 15).forEach((s) => log(`  • ${s.calibreName}`));
        if (r.candidates > r.samples.length) {
          log(`  …and ${r.candidates - r.samples.length} more`);
        }
        log(
          r.candidates === 0
            ? 'Nothing to backfill.'
            : 'Turn off “Dry run” and press Run to enrich these (one AI call each, auto-accepts tiers).',
        );
      } else {
        let batch = 0;
        let totalProcessed = 0;
        let totalUpdated = 0;
        let totalTiers = 0;
        let totalErrors = 0;
        // Belt-and-braces cap so a misbehaving hasMore can never loop forever.
        const MAX_BATCHES = 500;
        for (;;) {
          batch += 1;
          const r = await backfillCalibreTiers({ dryRun: false });
          totalProcessed += r.processed;
          totalUpdated += r.calibresUpdated;
          totalTiers += r.tiersAdded;
          totalErrors += r.errors.length;
          log(
            `Batch ${batch}: enriched ${r.processed}, added ${r.tiersAdded} tier(s) on ${r.calibresUpdated} calibre(s)` +
              (r.errors.length ? `, ${r.errors.length} error(s)` : '') +
              (r.hasMore ? ' — more remain…' : '.'),
          );
          r.samples
            .filter((s) => s.tiersAdded > 0)
            .slice(0, 8)
            .forEach((s) => log(`  • ${s.calibreName}: ${s.tierNames.join(', ')}`));
          // Stop if the server signals completion, made no progress (defensive
          // against a stuck hasMore), or we hit the batch cap.
          if (!r.hasMore || r.processed === 0 || batch >= MAX_BATCHES) break;
        }
        log(
          `Done — enriched ${totalProcessed} calibre(s); added ${totalTiers} tier(s) across ${totalUpdated} calibre(s)` +
            (totalErrors ? `; ${totalErrors} error(s)` : '') + '.',
        );
        toast.success(`Backfill complete: +${totalTiers} tier(s) on ${totalUpdated} calibre(s)`);
        await handleRefresh();
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Backfill failed';
      log(`Error: ${msg}`);
      toast.error(msg);
    } finally {
      setTiersBackfillRunning(false);
    }
  };

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  // Load the manufacturer catalogue once so rows can resolve manufacturerId →
  // name, and so the generic/non-generic Type filter can read `isGeneric` — a
  // field that lives on the manufacturer, not on any calibre record.
  //
  // From `watchkeeper_manufacturers` since PR #406. This was the last Firestore
  // collection read left on this tab after the calibre listing moved to Algolia
  // (#404): both `name` and `isGeneric` are indexed, and `classifyCalibreGeneric`
  // needs nothing else off these entities.
  //
  // Through the shared hook, so a manufacturer created from the calibre editor's
  // quick-create — or anywhere else — resolves here immediately instead of
  // rendering as a raw slug until the next page load.
  const { manufacturers: manufacturerCatalogue, loading: manufacturersLoading } =
    useManufacturerCatalogue();

  // The catalogue arrives sorted by canonical `name`; the bulk-assign dropdown
  // shows the display label, so it orders by that instead — as it always has.
  const manufacturers = useMemo(
    () =>
      [...manufacturerCatalogue].sort((a, b) =>
        (a.displayName || a.name).localeCompare(b.displayName || b.name),
      ),
    [manufacturerCatalogue],
  );

  const manufacturerNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const x of manufacturers) m.set(x.id, x.displayName || x.name);
    return m;
  }, [manufacturers]);

  const manufacturerById = useMemo(() => {
    const m = new Map<string, ManufacturerCatalogueEntry>();
    for (const x of manufacturers) m.set(x.id, x);
    return m;
  }, [manufacturers]);

  // Deprecated calibres are redirect aliases (reclassified into a tier of
  // another calibre). Hidden by default — matching what `getCustomCalibres()`
  // did before this listing moved to Algolia — but fetched, so the toggle is
  // instant and the count below is honest. Switching it on ISOLATES them rather
  // than merging them into the alphabetical list, where they were unfindable.
  const visibleRows = useMemo(
    () => filterCalibresByDeprecated(rows, showDeprecated),
    [rows, showDeprecated],
  );
  const deprecatedCount = useMemo(
    () => rows.reduce((n, r) => n + (r.deprecated ? 1 : 0), 0),
    [rows],
  );

  // Stats — computed over the rows the table is actually showing, so they can
  // never disagree with the list the way index-wide facet counts would.
  const totalCount = visibleRows.length;
  const withImagesCount = useMemo(
    () => visibleRows.filter((r) => r.hasImage).length,
    [visibleRows],
  );
  const fromCalibreCornerCount = useMemo(
    () => visibleRows.filter((r) => r.source === 'calibercorner').length,
    [visibleRows],
  );

  // Brand options for the group dropdown — distinct lenient-keys (canonical
  // brandId, falling back to the legacy free-text brandName).
  const brandOptions = useMemo(() => {
    const set = new Set<string>();
    visibleRows.forEach((r) => {
      const k = calibreRowBrandKey(r);
      if (k) set.add(k);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [visibleRows]);

  // Manufacturer options — only makers with a calibre in view, each with its
  // count. Follows the Show deprecated toggle, as the brand options do.
  const manufacturerFilterOptions = useMemo(
    () => buildManufacturerFilterOptions(visibleRows, manufacturerNameById),
    [visibleRows, manufacturerNameById],
  );
  const activeManufacturerOption = manufacturerFilterOptions.options.find((o) => o.id === manufacturerFilter);
  const showAllForManufacturer = showAllChoice ?? defaultShowAll(activeManufacturerOption?.primaryCount ?? 0);


  /**
   * The search box, matched by Algolia rather than by substring.
   *
   * `normalizeCalibreKey().includes()` over name/aliases/brand demanded near-
   * exact spelling and could not see past the calibre's own fields at all.
   * Algolia adds typo tolerance, prefix matching and accent folding, and — since
   * watchlock #864 — searches the denormalised `manufacturerName`, `brandNames`
   * and `familyName`, so typing "sellita" surfaces every calibre that maker
   * built rather than only the ones with "Sellita" in their name.
   *
   * Only MATCHING moves server-side; ORDER stays local, because this listing has
   * its own sort control and re-sorts whatever survives the filters anyway. The
   * hook falls back to `listRowMatches` — the exact predicate the box used
   * before — whenever Algolia can't answer.
   */
  const { results: searchedRows, searching, activeTerm } = useCalibreListSearch(
    visibleRows,
    searchTerm,
    { matches: listRowMatches },
  );

  /**
   * Relevance sort. `searchedRows` already arrives in Algolia's rank order —
   * `mergeCalibreSearchResults` emits hits in hit order — so "relevance" is the
   * local re-sort below being SKIPPED, not a different query. See
   * `services/relevanceSort.ts`.
   *
   * Keyed off `activeTerm` (the debounced term the rows are for) rather than the
   * box, so the order flips with the ranked rows instead of a beat before them.
   */
  const {
    relevanceActive,
    effectiveSortField,
    relevanceAvailable,
    pickSort,
  } = useRelevanceSort<SortField>({
    query: activeTerm,
    sortField,
    defaultSortField: DEFAULT_SORT_FIELD,
    setSortField: (field) => {
      setSortField(field);
      setCurrentPage(1);
    },
  });

  // Classify from the whole manufacturer SET (primary + additionalManufacturerIds),
  // so a calibre co-developed by a generic maker (ETA) and a non-generic one
  // (Officine Panerai) reads as non-generic — the non-generic co-developer wins.
  // 'unknown' still bundles: no manufacturer FK, none resolve, or none carry an
  // isGeneric flag. See services/calibreClassification.ts.
  //
  // This is what watchlock #865 unblocked for the Algolia listing: the raw
  // `additionalManufacturerIds` array is now on the record, so a row classifies
  // identically to the document it projects. Before it, only the ancestor-
  // inflated `allManufacturerIds` superset was indexed and every co-developed
  // calibre would have been judged by its primary maker alone.
  const classifyGeneric = (r: CalibreListRow): GenericFilter | 'unknown' =>
    classifyCalibreGeneric(r, manufacturerById);

  // Split out so the "hidden (unknown type)" count can be computed against
  // the same search+group result without re-running the filter.
  const preGenericResult = useMemo(() => {
    let result = searchedRows;

    // Group filter (when groupBy is a specific brand value, filter to that brand)
    if (groupBy && groupBy !== '__group_brand__') {
      result = result.filter(
        (r) => calibreRowBrandKey(r).toLowerCase() === groupBy.toLowerCase(),
      );
    }

    // Manufacturer filter — asymmetric (James, 2026-11-03): primary only, or
    // everything associated (co-developer / original maker) with "Show all".
    // Composes with the brand filter above.
    if (manufacturerFilter) {
      result = result.filter((r) => calibreMatchesManufacturerFilter(r, manufacturerFilter, showAllForManufacturer));
    }

    return result;
  }, [searchedRows, groupBy, manufacturerFilter, showAllForManufacturer]);

  // Filter + sort
  const processed = useMemo(() => {
    let result = preGenericResult;

    if (genericFilter !== 'all') {
      result = result.filter((r) => classifyGeneric(r) === genericFilter);
    } else {
      result = [...result];
    }

    // Sort — unless relevance is on, in which case the order the search ranked
    // is the answer. The filters above are all `.filter`, so it survives them.
    if (relevanceActive) return result;

    result.sort((a, b) => {
      let cmp = 0;
      if (effectiveSortField === 'name') {
        cmp = a.name.localeCompare(b.name);
      } else if (effectiveSortField === 'brand') {
        cmp = calibreRowBrandKey(a).localeCompare(calibreRowBrandKey(b));
      } else if (effectiveSortField === 'manufacturer') {
        const aName = (a.manufacturerId && manufacturerNameById.get(a.manufacturerId)) || '';
        const bName = (b.manufacturerId && manufacturerNameById.get(b.manufacturerId)) || '';
        // Unresolved/empty manufacturers always sort last, regardless of sortDir.
        if (!aName && !bName) cmp = 0;
        else if (!aName) return 1;
        else if (!bName) return -1;
        else cmp = aName.localeCompare(bName);
      } else if (effectiveSortField === 'hasImage') {
        cmp = Number(b.hasImage) - Number(a.hasImage);
      } else if (effectiveSortField === 'refs') {
        cmp = a.referenceCount - b.referenceCount;
      } else if (effectiveSortField === 'createdAt') {
        const aMs = a.createdAt?.toMillis?.() ?? 0;
        const bMs = b.createdAt?.toMillis?.() ?? 0;
        cmp = aMs - bMs;
      }
      return sortDir === 'desc' ? -cmp : cmp;
    });

    return result;
    // manufacturerById/manufacturerNameById are derived from `manufacturers`;
    // listing the maps keeps sort/classify reactive to manufacturer loads.
  }, [preGenericResult, genericFilter, relevanceActive, effectiveSortField, sortDir, manufacturerById, manufacturerNameById]);

  const hiddenUnknownCount = useMemo(() => {
    if (genericFilter === 'all') return 0;
    return preGenericResult.reduce(
      (n, r) => n + (classifyGeneric(r) === 'unknown' ? 1 : 0),
      0,
    );
  }, [preGenericResult, genericFilter, manufacturerById]);

  const totalPages = Math.max(1, Math.ceil(processed.length / pageSize));
  const pageItems = processed.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  // Selection helpers — header checkbox acts on the *filtered* set (matches Movement Manufacturers).
  const allFilteredSelected =
    processed.length > 0 && processed.every((r) => selected.has(r.id));
  const someFilteredSelected =
    processed.some((r) => selected.has(r.id)) && !allFilteredSelected;

  // ...and the footer checkbox acts on the current PAGE only. Its own scope
  // rather than a radio partner to the header one: on a 1,800-row catalogue the
  // useful bulk unit is often the 25 rows you are looking at, not every row the
  // filters left behind. Both the tick state and the count derive from
  // `pageItems`, so the last page's short slice reports and selects exactly its
  // own rows, and changing page or page size recomputes rather than going stale.
  const allPageSelected =
    pageItems.length > 0 && pageItems.every((r) => selected.has(r.id));
  const somePageSelected =
    pageItems.some((r) => selected.has(r.id)) && !allPageSelected;

  const selectedCount = selected.size;

  const toggleSelect = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleSelectAllFiltered = () =>
    setSelected((prev) => toggleRowsInSelection(prev, processed));

  const toggleSelectPage = () =>
    setSelected((prev) => toggleRowsInSelection(prev, pageItems));

  const clearSelection = () => setSelected(new Set());

  /**
   * Read the selection back as full Firestore documents.
   *
   * The gate before every tool that consumes fields the index doesn't carry.
   * One document read per selected calibre, and only for the tools that need it
   * — a selection of 20 costs 20 reads, against the ~1,822 this page used to
   * spend just to render.
   *
   * Returns null (and reports) on failure, so a caller opens nothing rather than
   * opening a tool over an empty or partial selection.
   */
  const hydrateSelection = async (): Promise<CustomCalibreEntry[] | null> => {
    const ids = [...selected];
    if (ids.length === 0) return null;
    setHydrating(true);
    try {
      const docs = await getCustomCalibresByIds(ids);
      if (docs.length === 0) {
        toast.error('Could not load the selected calibres');
        return null;
      }
      if (docs.length < ids.length) {
        toast(
          `${ids.length - docs.length} selected calibre(s) could not be loaded and will be skipped`,
        );
      }
      setSelectionDocs(docs);
      return docs;
    } catch (error) {
      console.error('Failed to load selected calibres:', error);
      toast.error('Could not load the selected calibres');
      return null;
    } finally {
      setHydrating(false);
    }
  };

  const openBulkSetManufacturer = () => {
    setBulkTargetMfrId('');
    setBulkSetMfrOpen(true);
  };

  /** The selected rows, in listing order. `manufacturerId` and `brandId` are indexed. */
  const selectedRows = useMemo(
    () => rows.filter((r) => selected.has(r.id)),
    [rows, selected],
  );

  /**
   * Whether the two brand-filling actions (Set Brand, Set AKA Brands) can run.
   *
   * Both derive their target brand from the calibre's MANUFACTURER SET, so both
   * require every selected calibre to have a non-generic maker: a generic maker
   * (ETA, Sellita) doesn't make a calibre belong to a brand, and an unset or
   * unresolved one says nothing at all. All-or-nothing over the selection rather
   * than "apply to whichever qualify" — a bulk action that silently skips half
   * the rows it was pointed at is how a brand gets set on a calibre nobody
   * checked.
   *
   * Reads the rows, not hydrated documents: both manufacturer fields are indexed
   * attributes, so this costs nothing.
   */
  const brandActions = useMemo(() => {
    if (selectedCount === 0) return { enabled: false, reason: 'Nothing selected' };
    if (manufacturersLoading) {
      return { enabled: false, reason: 'Loading manufacturers…' };
    }
    // The selection is sticky across reloads, so it can outlive a row. Both
    // actions plan off `selectedRows`, and quietly planning over fewer calibres
    // than the bar says are selected is exactly the kind of silent narrowing
    // these confirm modals exist to prevent.
    if (selectedRows.length !== selectedCount) {
      return {
        enabled: false,
        reason:
          `${selectedCount - selectedRows.length} selected calibre(s) are no longer in the ` +
          'loaded listing. Refresh, or clear and re-select.',
      };
    }
    const ineligible = selectedRows.filter(
      (r) => classifyCalibreGeneric(r, manufacturerById) !== 'non-generic',
    );
    if (ineligible.length > 0) {
      return {
        enabled: false,
        reason:
          `${ineligible.length} of ${selectedRows.length} selected calibre${selectedRows.length === 1 ? '' : 's'} ` +
          `${ineligible.length === 1 ? 'has' : 'have'} a generic or unset manufacturer. ` +
          'Every selected calibre needs a non-generic manufacturer before a brand can be derived.',
      };
    }
    return { enabled: true, reason: '' };
  }, [selectedCount, selectedRows, manufacturerById, manufacturersLoading]);

  /**
   * Resolve each selected calibre's target brand, plus the display names to
   * report it with.
   *
   * The derivation itself is `defaultAkaBrandId` — the SAME helper the calibre
   * editor pre-fills a new alias with, so these bulk actions can never disagree
   * with what the editor would have done one row at a time.
   *
   * The two Firestore reads are both `getWatchBrandsByIds` (an `in` query per 30
   * ids, not a collection scan):
   *  1. Over the selection's MANUFACTURER ids — which of them exist as watchBrand
   *     docs. That is the `brandIdExists` predicate the helper takes, and it is
   *     what covers the in-house case where a manufacturer's doc id IS a brand
   *     slug (Rolex, IWC). Without it an in-house maker with no curated
   *     allowlist and no `primaryBrandId` would resolve to nothing.
   *  2. Over the resolved TARGET ids, for names the first read didn't already
   *     supply (it does whenever the target came from the in-house case).
   *
   * Returns null (and reports) on failure, so the caller opens no modal.
   */
  const resolveBrandTargets = useCallback(
    async (
      entries: (CalibreManufacturerRefs & { id: string })[],
    ): Promise<{
      targetFor: (calibreId: string) => string | undefined;
      nameById: Map<string, string>;
    } | null> => {
      setHydrating(true);
      try {
        const mfrIds = [
          ...new Set(entries.flatMap((e) => calibreManufacturerIds(e))),
        ];
        const nameById = new Map<string, string>();
        const brandSlugs = new Set<string>();
        for (const b of await getWatchBrandsByIds(mfrIds)) {
          brandSlugs.add(b.id);
          nameById.set(b.id, b.name || b.id);
        }

        const targets = new Map<string, string>();
        for (const e of entries) {
          const target = defaultAkaBrandId(e, manufacturerById, (id) =>
            brandSlugs.has(id),
          );
          if (target) targets.set(e.id, target);
        }

        const missingNames = [...new Set(targets.values())].filter(
          (id) => !nameById.has(id),
        );
        if (missingNames.length > 0) {
          for (const b of await getWatchBrandsByIds(missingNames)) {
            nameById.set(b.id, b.name || b.id);
          }
        }
        return { targetFor: (calibreId) => targets.get(calibreId), nameById };
      } catch (error) {
        console.error('Failed to resolve target brands:', error);
        toast.error('Could not resolve the target brands');
        return null;
      } finally {
        setHydrating(false);
      }
    },
    [manufacturerById],
  );

  // Needs no hydration: `brandId` and both manufacturer fields are indexed, so
  // the whole plan can be built from the rows already on screen.
  const openBulkSetBrand = async () => {
    const resolved = await resolveBrandTargets(selectedRows);
    if (!resolved) return;
    setBrandNameById(resolved.nameById);
    setBrandPlan(planCalibreBrandUpdates(selectedRows, resolved.targetFor));
    setBulkSetBrandOpen(true);
  };

  // Needs the real `aliasDetails` — the index carries only the flat
  // `alsoKnownAs` names, with none of their brand scope, so a plan built from
  // rows would rewrite every alias list from scratch.
  const openBulkSetAkaBrands = async () => {
    const docs = await hydrateSelection();
    if (!docs) return;
    const resolved = await resolveBrandTargets(docs);
    if (!resolved) return;
    setBrandNameById(resolved.nameById);
    setAkaPlan(planCalibreAkaBrandUpdates(docs, resolved.targetFor));
    setBulkAkaBrandsOpen(true);
  };

  const brandLabel = useCallback(
    (brandId?: string) => (brandId ? brandNameById.get(brandId) || brandId : '—'),
    [brandNameById],
  );

  // Needs the real `images` array — the row carries one resolved URL, not the
  // entries this write replaces.
  const openBulkSetImageSource = async () => {
    if (!(await hydrateSelection())) return;
    setBulkTargetImgSrc('');
    setBulkImgSrcOpen(true);
  };

  const openBulkSetCalibreSource = () => {
    setBulkTargetCalSrc('');
    setBulkCalSrcOpen(true);
  };

  // The AI tool reads and writes back the whole of `data.*`, none of which is on
  // a listing row.
  const openAiEnhance = async () => {
    if (!(await hydrateSelection())) return;
    setAiEnhanceOpen(true);
  };

  // Image stats across the hydrated selection — drives the confirm/warning copy.
  const selectedImageStats = useMemo(() => {
    if (!bulkImgSrcOpen) return { withImages: 0, withoutImages: 0, totalImages: 0 };
    let withImages = 0;
    let withoutImages = 0;
    let totalImages = 0;
    for (const e of selectionDocs) {
      const n = e.images?.length ?? 0;
      if (n > 0) {
        withImages++;
        totalImages += n;
      } else {
        withoutImages++;
      }
    }
    return { withImages, withoutImages, totalImages };
  }, [bulkImgSrcOpen, selectionDocs]);

  const handleBulkApplyImageSource = async () => {
    if (!bulkTargetImgSrc || selectionDocs.length === 0) return;
    const { withImages, totalImages } = selectedImageStats;
    if (withImages === 0) {
      toast.error('None of the selected calibres have images to update');
      return;
    }
    const ok = confirm(
      `Set image source to "${bulkTargetImgSrc}" on ${withImages} calibre${withImages === 1 ? '' : 's'}? ` +
        `This will overwrite the current source value on ${totalImages} image${totalImages === 1 ? '' : 's'}.`,
    );
    if (!ok) return;
    setBulkRunning(true);
    try {
      const res = await bulkSetCalibreImageSource(
        selectionDocs.map((e) => ({ id: e.id, images: e.images })),
        bulkTargetImgSrc,
      );
      if (res.failedIds.length > 0) {
        toast.error(
          `Updated ${res.calibresUpdated} of ${res.calibresUpdated + res.failedIds.length}; ` +
            `${res.failedIds.length} failed`,
        );
      } else {
        toast.success(
          `Updated source on ${res.calibresUpdated} calibre${res.calibresUpdated === 1 ? '' : 's'} / ` +
            `${res.imagesUpdated} image${res.imagesUpdated === 1 ? '' : 's'}`,
        );
      }
      setBulkImgSrcOpen(false);
      clearSelection();
      // Deliberately no reload: per-image `source` is not a listing column, so
      // re-fetching every record would show exactly the same table.
    } catch (err) {
      console.error('Bulk set image source failed:', err);
      toast.error(err instanceof Error ? err.message : 'Bulk update failed');
    } finally {
      setBulkRunning(false);
    }
  };

  const handleBulkApplyCalibreSource = async () => {
    if (!bulkTargetCalSrc || selectedCount === 0) return;
    const targetIds = [...selected];
    const ok = confirm(
      `Set calibre source to "${bulkTargetCalSrc}" on ${targetIds.length} calibre${targetIds.length === 1 ? '' : 's'}? ` +
        `This will overwrite the current calibre-level source value.`,
    );
    if (!ok) return;
    setBulkRunning(true);
    try {
      const res = await bulkSetCalibreSource(targetIds, bulkTargetCalSrc);
      if (res.failedIds.length > 0) {
        toast.error(
          `Updated ${res.calibresUpdated} of ${res.calibresUpdated + res.failedIds.length}; ` +
            `${res.failedIds.length} failed`,
        );
      } else {
        toast.success(
          `Updated calibre source on ${res.calibresUpdated} calibre${res.calibresUpdated === 1 ? '' : 's'}`,
        );
      }
      setBulkCalSrcOpen(false);
      clearSelection();
      await loadRows();
      // Re-assert what we know landed: the reload can still be serving records
      // the indexer hasn't rewritten yet.
      const written = targetIds.filter((id) => !res.failedIds.includes(id));
      patchRows(written, { source: bulkTargetCalSrc });
    } catch (err) {
      console.error('Bulk set calibre source failed:', err);
      toast.error(err instanceof Error ? err.message : 'Bulk update failed');
    } finally {
      setBulkRunning(false);
    }
  };

  // Bulk Set Manufacturer NEVER overwrites an already-set manufacturer (per
  // James: "should never overwrite an already set manufacturer"). Split the
  // selection into the calibres we'll actually write (manufacturerId empty/unset)
  // and the ones we silently skip (already have one). This rule is unconditional
  // — not toggleable.
  //
  // Reads `manufacturerId` straight off the rows: it IS indexed, so this tool
  // needs no hydration pass.
  const bulkMfrTargets = useMemo(() => {
    if (!bulkSetMfrOpen) return { eligibleIds: [] as string[], skipCount: 0 };
    const eligibleIds: string[] = [];
    let skipCount = 0;
    for (const r of rows) {
      if (!selected.has(r.id)) continue;
      if ((r.manufacturerId || '').trim()) skipCount++;
      else eligibleIds.push(r.id);
    }
    return { eligibleIds, skipCount };
  }, [bulkSetMfrOpen, rows, selected]);

  const handleBulkApply = async () => {
    if (!bulkTargetMfrId || selectedCount === 0) return;
    const { eligibleIds, skipCount } = bulkMfrTargets;
    if (eligibleIds.length === 0) {
      toast.error('All selected calibres already have a manufacturer — nothing to set');
      return;
    }
    setBulkRunning(true);
    try {
      await bulkSetCalibreManufacturer(eligibleIds, bulkTargetMfrId);
      toast.success(
        `Set manufacturer on ${eligibleIds.length} calibre${eligibleIds.length === 1 ? '' : 's'}` +
          (skipCount > 0 ? ` (${skipCount} skipped — already set)` : ''),
      );
      setBulkSetMfrOpen(false);
      // Selection deliberately SURVIVES this apply (unlike the bulk-source and
      // bulk-delete handlers above): setting a manufacturer is the first of a
      // chain — Set Brand, Set Default Brand on Unbranded AKAs — over the same
      // rows, and re-ticking them between each step was pure friction. Re-running
      // Set Manufacturer on the still-selected rows is harmless: `bulkMfrTargets`
      // recomputes off the patched rows, so the now-set ones count as skips.
      // "Clear" in the selection bar is the way out.
      await loadRows();
      patchRows(eligibleIds, { manufacturerId: bulkTargetMfrId });
    } catch (err) {
      console.error('Bulk set manufacturer failed:', err);
      toast.error(err instanceof Error ? err.message : 'Bulk update failed');
    } finally {
      setBulkRunning(false);
    }
  };

  /**
   * Set Brand — fill each calibre's own `brandId` from its manufacturer's
   * default brand.
   *
   * Writes only the plan's `writes` bucket: a calibre already scoped to the
   * target is left alone, and one scoped to a DIFFERENT brand is skipped rather
   * than reassigned. The legacy free-text `brandName` is filled only where it is
   * empty — `brandId` is what the listing and the RN app prefer, so an existing
   * string is left as somebody's data rather than rewritten.
   */
  const handleBulkApplyBrand = async () => {
    if (!brandPlan || brandPlan.writes.length === 0) return;
    setBulkRunning(true);
    try {
      const rowById = new Map(rows.map((r) => [r.id, r]));
      const writes = brandPlan.writes.map((w) => {
        const brandId = w.targetBrandId!;
        const existingName = (rowById.get(w.id)?.brandName || '').trim();
        const resolvedName = brandNameById.get(brandId);
        return {
          id: w.id,
          brandId,
          brandName: existingName ? undefined : resolvedName,
        };
      });
      await bulkSetCalibreBrand(writes);

      const skipped = [
        brandPlan.alreadySet.length > 0
          ? `${brandPlan.alreadySet.length} already set`
          : '',
        brandPlan.conflicts.length > 0
          ? `${brandPlan.conflicts.length} scoped to another brand`
          : '',
        brandPlan.noTarget.length > 0
          ? `${brandPlan.noTarget.length} with no default brand`
          : '',
      ].filter(Boolean);
      toast.success(
        `Set brand on ${writes.length} calibre${writes.length === 1 ? '' : 's'}` +
          (skipped.length > 0 ? ` (skipped: ${skipped.join(', ')})` : ''),
      );
      setBulkSetBrandOpen(false);
      // Selection SURVIVES, like Set Manufacturer: this is the middle step of
      // the Set Manufacturer → Set Brand → Set AKA Brands chain over one set of
      // rows. Re-running it is harmless — the rows just written now plan as
      // "already set".
      await loadRows();
      // Re-assert what landed; the reload can still be serving pre-write records.
      const byBrand = new Map<string, string[]>();
      for (const w of writes) {
        const ids = byBrand.get(w.brandId) ?? [];
        ids.push(w.id);
        byBrand.set(w.brandId, ids);
      }
      for (const [brandId, ids] of byBrand) {
        patchRows(ids, { brandId, brandName: brandNameById.get(brandId) });
      }
    } catch (err) {
      console.error('Bulk set brand failed:', err);
      toast.error(err instanceof Error ? err.message : 'Bulk update failed');
    } finally {
      setBulkRunning(false);
    }
  };

  /**
   * Set AKA Brands — scope every UNBRANDED alias on each selected calibre to its
   * manufacturer's default brand.
   *
   * Fill-empty only: an alias that already names a brand — the target one or any
   * other — is written back exactly as it was. The calibre's own `brandId` is
   * not touched here; that is the Set Brand action next to it.
   */
  const handleBulkApplyAkaBrands = async () => {
    if (!akaPlan || akaPlan.writes.length === 0) return;
    setBulkRunning(true);
    try {
      await bulkSetCalibreAliasBrands(
        akaPlan.writes.map((w) => ({ id: w.id, aliasDetails: w.aliasDetails })),
      );
      const calibres = akaPlan.writes.length;
      toast.success(
        `${akaPlan.aliasesUpdated} AKA${akaPlan.aliasesUpdated === 1 ? '' : 's'} updated across ` +
          `${calibres} calibre${calibres === 1 ? '' : 's'}` +
          (akaPlan.nothingToDo.length > 0
            ? `, ${akaPlan.nothingToDo.length} calibre${akaPlan.nothingToDo.length === 1 ? '' : 's'} had no unbranded AKAs to update`
            : '') +
          (akaPlan.noTarget.length > 0
            ? `, ${akaPlan.noTarget.length} skipped with no default brand`
            : ''),
      );
      setBulkAkaBrandsOpen(false);
      // Selection survives, and deliberately no reload: alias brand SCOPE is on
      // no listing column (the index carries the flat names only), so re-fetching
      // every record would redraw exactly the same table.
    } catch (err) {
      console.error('Bulk set AKA brands failed:', err);
      toast.error(err instanceof Error ? err.message : 'Bulk update failed');
    } finally {
      setBulkRunning(false);
    }
  };

  // When grouping by brand (not filtering to a single brand), show grouped sections within the page
  const isGroupByBrand = groupBy === '__group_brand__';
  const groupedPageItems = useMemo(() => {
    if (!isGroupByBrand) return null;
    const groups = new Map<string, CalibreListRow[]>();
    pageItems.forEach((r) => {
      const key = calibreRowBrandKey(r) || '— No brand —';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(r);
    });
    return Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [pageItems, isGroupByBrand]);

  // Clicking a header is a manual pick — `pickSort` records that, which is what
  // stops the next query auto-switching back to relevance.
  const toggleSort = (field: SortField) => {
    if (effectiveSortField === field && !relevanceActive) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
      pickSort(field);
    } else {
      pickSort(field);
      setSortDir(field === 'createdAt' || field === 'refs' ? 'desc' : 'asc');
    }
    setCurrentPage(1);
  };

  /**
   * Open the editor on a freshly read document, never on a listing row.
   *
   * The editor writes the WHOLE form back, so opening it on an Algolia
   * projection would save blanks over `images`, `aliasDetails`, `data.tiers`,
   * `description` and everything else the index doesn't carry.
   */
  const openEditor = useCallback(async (row: CalibreListRow) => {
    setHydrating(true);
    try {
      const full = await getCustomCalibreById(row.id);
      if (!full) {
        toast.error(`"${row.name}" no longer exists`);
        setRows((prev) => prev.filter((r) => r.id !== row.id));
        return;
      }
      setModalEntry(full);
      setModalCreate(false);
      setModalOpen(true);
    } catch (error) {
      console.error('Failed to load calibre document:', error);
      toast.error(`Could not load "${row.name}"`);
    } finally {
      setHydrating(false);
    }
  }, []);

  // Deep-link target (e.g. from the Description Audit's "Open in Calibre Editor"):
  // once the list has loaded, open the matching calibre's editor modal, then tell
  // the parent to clear the target so it doesn't re-open on every render.
  useEffect(() => {
    if (!openCalibreId || loading) return;
    const row = rows.find((r) => r.id === openCalibreId);
    if (row) {
      void openEditor(row);
      setSearchTerm(row.name || openCalibreId);
    } else {
      toast.error(`Calibre ${openCalibreId} not found in the list`);
    }
    onOpenCalibreConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openCalibreId, loading, rows]);

  const handleAdd = () => {
    setModalEntry(null);
    setModalCreate(true);
    setModalOpen(true);
  };

  const handleDelete = async (row: CalibreListRow) => {
    if (!confirm(`Delete "${row.name}"? This cannot be undone.`)) return;
    setDeletingId(row.id);
    try {
      await deleteCustomCalibre(row.id);
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      toast.success(`Deleted "${row.name}"`);
    } catch (error) {
      console.error('Failed to delete:', error);
      toast.error('Failed to delete calibre');
    } finally {
      setDeletingId(null);
    }
  };


  const handleModalClose = () => {
    setModalOpen(false);
    setModalEntry(null);
    setModalCreate(false);
  };

  if (loading) {
    return (
      <div className='flex justify-center py-12'>
        <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600'></div>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex justify-between items-start flex-wrap gap-3'>
        <div>
          <h1 className='text-2xl font-bold text-gray-900'>Curated Calibres</h1>
          <p className='text-gray-500 mt-1'>Manage the custom calibre database (in-house and CalibreCorner-sourced movements).</p>
        </div>
      </div>

      {/* Stats */}
      <div className='flex items-center gap-3 flex-wrap'>
        <StatPill label='Total calibres' value={totalCount} />
        <StatPill label='With images' value={withImagesCount} accent='blue' />
        <StatPill label='From CalibreCorner' value={fromCalibreCornerCount} accent='emerald' />
        {degraded && (
          <span
            className='inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-amber-50 text-amber-700 border border-amber-200'
            title='Search is unavailable, so this list came from Firestore and the search box is matching by substring only'
          >
            Loaded without search index
          </span>
        )}
      </div>

      {/* Toolbar */}
      <div className='flex gap-3 items-center flex-wrap'>
        <div className='relative flex-1 min-w-[240px]'>
          <input
            type='text'
            placeholder='Search by name, alias, brand, or manufacturer…'
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            className='w-full border border-gray-300 rounded-lg px-4 py-2 pl-10 focus:ring-2 focus:ring-brand-500 focus:border-brand-500'
          />
          <svg className='absolute left-3 top-2.5 h-5 w-5 text-gray-400' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z' />
          </svg>
          {searching && (
            <span className='absolute right-3 top-2.5 text-xs text-gray-400'>Searching…</span>
          )}
        </div>

        {/* Only while a query is running — there is no ranking without one. */}
        {relevanceAvailable && (
          <RelevanceSortToggle
            active={relevanceActive}
            fallbackLabel='name'
            onToggle={() =>
              pickSort(relevanceActive ? DEFAULT_SORT_FIELD : RELEVANCE_SORT)
            }
          />
        )}

        <div className='flex items-center gap-2'>
          <label className='text-sm text-gray-600'>Group:</label>
          <select
            value={groupBy}
            onChange={(e) => { setGroupBy(e.target.value); setCurrentPage(1); }}
            className='border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500'
          >
            <option value=''>All (flat list)</option>
            <option value='__group_brand__'>Group by brand</option>
            <optgroup label='Filter by brand'>
              {brandOptions.map((b) => (
                <option key={b} value={b}>{b}</option>
              ))}
            </optgroup>
          </select>
        </div>

        <div className='flex items-center gap-2'>
          <label htmlFor='calibre-manufacturer-filter' className='text-sm text-gray-600'>Manufacturer:</label>
          <select
            id='calibre-manufacturer-filter'
            value={manufacturerFilter}
            onChange={(e) => { setManufacturerFilter(e.target.value); setShowAllChoice(null); setCurrentPage(1); }}
            className='border border-gray-300 rounded-lg px-3 py-2 text-sm max-w-[16rem] focus:ring-2 focus:ring-brand-500 focus:border-brand-500'
          >
            <option value=''>All manufacturers</option>
            {manufacturerFilterOptions.noManufacturerCount > 0 && (
              <option value={NO_MANUFACTURER_FILTER}>
                No manufacturer ({manufacturerFilterOptions.noManufacturerCount})
              </option>
            )}
            {manufacturerFilterOptions.options.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label} ({o.count})
              </option>
            ))}
            {/* Keep the active choice selectable when the view no longer holds
                any of its calibres (e.g. after toggling Show deprecated). */}
            {manufacturerFilter &&
              manufacturerFilter !== NO_MANUFACTURER_FILTER &&
              !manufacturerFilterOptions.options.some((o) => o.id === manufacturerFilter) && (
                <option value={manufacturerFilter}>
                  {manufacturerNameById.get(manufacturerFilter) || manufacturerFilter} (0)
                </option>
              )}
            {manufacturerFilter === NO_MANUFACTURER_FILTER &&
              manufacturerFilterOptions.noManufacturerCount === 0 && (
                <option value={NO_MANUFACTURER_FILTER}>No manufacturer (0)</option>
              )}
          </select>
          {activeManufacturerOption && activeManufacturerOption.count > activeManufacturerOption.primaryCount && (
            <label
              className='flex items-center gap-1 text-xs text-gray-600'
              title='Off: calibres this maker makes today (primary). On: also the ones it co-developed or originally made. Starts on for a maker that is the primary of nothing today.'
            >
              <input
                type='checkbox'
                checked={showAllForManufacturer}
                onChange={(e) => { setShowAllChoice(e.target.checked); setCurrentPage(1); }}
                className='rounded border-gray-300'
              />
              Show all {activeManufacturerOption.count} calibres associated with this manufacturer
            </label>
          )}
        </div>

        <div className='flex items-center gap-2'>
          <label className='text-sm text-gray-600'>Type:</label>
          <select
            value={genericFilter}
            onChange={(e) => { setGenericFilter(e.target.value as GenericFilter); setCurrentPage(1); }}
            className='border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500'
          >
            <option value='all'>All</option>
            <option value='generic'>Generic only</option>
            <option value='non-generic'>Non-generic only</option>
          </select>
          {hiddenUnknownCount > 0 && (
            <span
              className='inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-amber-50 text-amber-700 border border-amber-200'
              title='Calibres whose manufacturer is unknown or has no generic/non-generic classification'
            >
              {hiddenUnknownCount} hidden (unknown type)
            </span>
          )}
        </div>

        <label
          className='flex items-center gap-2 text-sm text-gray-600'
          title='Show only deprecated calibres — redirect aliases reclassified as a brand-specific tier of another calibre'
        >
          <input
            type='checkbox'
            checked={showDeprecated}
            onChange={(e) => { setShowDeprecated(e.target.checked); setCurrentPage(1); }}
            className='rounded text-brand-600 focus:ring-brand-500'
          />
          Only deprecated ({deprecatedCount})
        </label>

        {/* The whole-collection sweep. Hidden once rows are ticked, because the
            bulk-action bar then offers the same tool scoped to the selection —
            two "Backfill AI tiers" buttons on screen at once, one of them a
            collection-wide AI spend, is exactly the mis-click to design out. */}
        {selectedCount === 0 && (
          <Button
            variant='secondary'
            onClick={openTiersBackfillForAll}
            title='Populate movement tiers across the whole collection, on calibres whose tiers are empty (select rows first to scope it)'
          >
            Backfill AI tiers
          </Button>
        )}
        <Button
          variant='secondary'
          onClick={() => setNeedsAssessmentOpen(true)}
          title='Lineage placeholders ("revision unspecified") whose brand-AKA references can still move to a specific revision'
        >
          Needs assessment
        </Button>
        <Button
          variant='secondary'
          onClick={() => { setHygieneLog([]); setHygieneOpen(true); }}
          title='Move aliases still stranded on deprecated calibres onto their redirect target, then strip and rename the source'
        >
          Repair deprecated
        </Button>
        <Button
          variant='secondary'
          onClick={handleRefresh}
          loading={refreshing}
          title='Reload the calibre list from the search index'
        >
          Refresh
        </Button>
        <Button onClick={handleAdd}>+ Add Calibre</Button>
      </div>

      <NeedsAssessmentModal
        isOpen={needsAssessmentOpen}
        onClose={() => setNeedsAssessmentOpen(false)}
        onOpenCalibre={(id) => {
          setNeedsAssessmentOpen(false);
          // openEditor re-reads the document by id; the name is only for its toast.
          void openEditor({ id, name: rows.find((r) => r.id === id)?.name ?? id } as CalibreListRow);
        }}
      />

      {/* Table */}
      <div className='bg-white shadow rounded-lg overflow-hidden'>
        {/* The table is wider than a phone: without its own horizontal scroll the
            rounded card's `overflow-hidden` simply clipped the right-hand columns,
            leaving Edit and Delete unreachable. Same wrapper as the Brands and
            Movement Manufacturers grids. */}
        <div className='overflow-x-auto'>
          <table className='min-w-full divide-y divide-gray-200'>
            <thead className='bg-gray-50 sticky top-0'>
              <tr>
                <th className='w-10 px-3 py-3'>
                  <input
                    type='checkbox'
                    checked={allFilteredSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = someFilteredSelected;
                    }}
                    onChange={toggleSelectAllFiltered}
                    aria-label='Select all filtered calibres'
                    className='rounded text-brand-600 focus:ring-brand-500'
                  />
                </th>
                <th
                  className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer select-none hover:text-gray-700'
                  onClick={() => toggleSort('name')}
                >
                  Name <SortIcon active={!relevanceActive && effectiveSortField === 'name'} dir={sortDir} />
                </th>
                <th
                  className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase w-32 cursor-pointer select-none hover:text-gray-700'
                  onClick={() => toggleSort('manufacturer')}
                >
                  Manufacturer <SortIcon active={!relevanceActive && effectiveSortField === 'manufacturer'} dir={sortDir} />
                </th>
                <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase'>Also Known As</th>
                <th className='px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase w-28'>Source</th>
                <th className='px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase w-14'>Jewels</th>
                <th className='px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase w-16'>Power</th>
                <th
                  className='px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase cursor-pointer select-none hover:text-gray-700'
                  onClick={() => toggleSort('refs')}
                  title='Parent and standalone references linked to this calibre by ID or matching it by name/alias. A warning shows how many match by text but are not linked yet.'
                >
                  Refs <SortIcon active={!relevanceActive && effectiveSortField === 'refs'} dir={sortDir} />
                </th>
                <th
                  className='px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase w-14 cursor-pointer select-none hover:text-gray-700'
                  onClick={() => toggleSort('hasImage')}
                  title='Sort by image presence'
                >
                  Image <SortIcon active={!relevanceActive && effectiveSortField === 'hasImage'} dir={sortDir} />
                </th>
                <th
                  className='px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer select-none hover:text-gray-700 w-28'
                  onClick={() => toggleSort('createdAt')}
                >
                  Added <SortIcon active={!relevanceActive && effectiveSortField === 'createdAt'} dir={sortDir} />
                </th>
                <th className='px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase w-24'>Actions</th>
              </tr>
            </thead>
            <tbody className='divide-y divide-gray-200'>
              {!isGroupByBrand && pageItems.map((row) => (
                <CalibreRow
                  key={row.id}
                  row={row}
                  onEdit={openEditor}
                  onDelete={handleDelete}
                  deleting={deletingId === row.id}
                  selected={selected.has(row.id)}
                  onToggleSelect={toggleSelect}
                  manufacturerNameById={manufacturerNameById}
                  browsingManufacturerId={manufacturerFilter || undefined}
                />
              ))}

              {isGroupByBrand && groupedPageItems && groupedPageItems.map(([brandKey, groupRows]) => (
                <GroupedRows
                  key={brandKey}
                  brandKey={brandKey}
                  rows={groupRows}
                  onEdit={openEditor}
                  onDelete={handleDelete}
                  deletingId={deletingId}
                  selected={selected}
                  onToggleSelect={toggleSelect}
                  manufacturerNameById={manufacturerNameById}
                  browsingManufacturerId={manufacturerFilter || undefined}
                />
              ))}
            </tbody>
          </table>
        </div>

        {pageItems.length === 0 && (
          <div className='text-center py-8 text-gray-500'>
            {searchTerm ? 'No calibres match your search' : 'No calibres yet — click "+ Add Calibre" to start.'}
          </div>
        )}
      </div>

      {/* Footer: count + page size + pagination */}
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-3'>
          <p className='text-sm text-gray-500'>
            {processed.length} of {visibleRows.length} calibres
          </p>
          <div className='flex items-center gap-1.5'>
            <label className='text-xs text-gray-500'>Show</label>
            <select
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
              className='border border-gray-300 rounded px-1.5 py-0.5 text-xs text-gray-600'
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </div>

          {/* Page-scoped selection. Lives here, next to the page size and the
              pager, because that is where the page as a unit is expressed —
              the whole-set tick stays on the table header where it has always
              been. */}
          <label
            className='flex items-center gap-1.5 text-xs text-gray-500 whitespace-nowrap cursor-pointer'
            title='Select just the calibres on this page — the checkbox in the table header selects every filtered calibre across all pages'
          >
            <input
              type='checkbox'
              checked={allPageSelected}
              ref={(el) => {
                if (el) el.indeterminate = somePageSelected;
              }}
              onChange={toggleSelectPage}
              disabled={pageItems.length === 0}
              className='rounded text-brand-600 focus:ring-brand-500 disabled:opacity-40'
            />
            Select page ({pageItems.length})
          </label>
        </div>
        {totalPages > 1 && (
          <div className='flex items-center gap-2'>
            <Button
              variant='secondary'
              size='sm'
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage(currentPage - 1)}
            >
              Previous
            </Button>
            <span className='text-sm text-gray-600'>Page {currentPage} of {totalPages}</span>
            <Button
              variant='secondary'
              size='sm'
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage(currentPage + 1)}
            >
              Next
            </Button>
          </div>
        )}
      </div>

      {/* ── Bulk actions over the current selection ─────────────────────────── */}
      {selectedCount > 0 && (
        <CalibreBulkActionBar
          count={selectedCount}
          busy={bulkRunning}
          hydrating={hydrating}
          brandActionsEnabled={brandActions.enabled}
          brandActionsDisabledReason={brandActions.reason}
          onAiEnhance={openAiEnhance}
          onSetManufacturer={openBulkSetManufacturer}
          onSetBrand={openBulkSetBrand}
          onSetAkaBrands={openBulkSetAkaBrands}
          onSetImageSource={openBulkSetImageSource}
          onSetCalibreSource={openBulkSetCalibreSource}
          onBackfillTiers={openTiersBackfillForSelection}
          onClearSelection={clearSelection}
        />
      )}

      {/* Bulk Set Manufacturer modal */}
      <Modal
        isOpen={bulkSetMfrOpen}
        onClose={() => { if (!bulkRunning) setBulkSetMfrOpen(false); }}
        title={`Set Manufacturer for ${selectedCount} selected calibre${selectedCount === 1 ? '' : 's'}`}
        size='md'
      >
        <div className='space-y-4'>
          <div>
            <label className='block text-sm font-medium text-gray-700 mb-1'>Manufacturer</label>
            <select
              value={bulkTargetMfrId}
              onChange={(e) => setBulkTargetMfrId(e.target.value)}
              disabled={manufacturersLoading || bulkRunning}
              className='w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:bg-gray-50 disabled:text-gray-500'
            >
              <option value=''>
                {manufacturersLoading ? 'Loading manufacturers…' : 'Choose a manufacturer…'}
              </option>
              {manufacturers.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.displayName || m.name}
                </option>
              ))}
            </select>
          </div>

          <div className='rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900'>
            <p>
              Will set <code className='font-mono text-xs'>manufacturerId</code> on{' '}
              <strong>{bulkMfrTargets.eligibleIds.length}</strong> calibre
              {bulkMfrTargets.eligibleIds.length === 1 ? '' : 's'}
              {bulkMfrTargets.skipCount > 0 && (
                <>
                  {' '}(<strong>{bulkMfrTargets.skipCount}</strong> already{' '}
                  {bulkMfrTargets.skipCount === 1 ? 'has' : 'have'} a manufacturer and will be skipped)
                </>
              )}
              .
            </p>
            <p className='mt-1 text-blue-800'>
              An already-set manufacturer is never overwritten.
            </p>
            {bulkMfrTargets.eligibleIds.length === 0 && (
              <p className='mt-1 text-amber-800'>
                Every selected calibre already has a manufacturer — nothing to set.
              </p>
            )}
          </div>

          <div className='flex justify-end gap-3 pt-2 border-t'>
            <Button
              variant='secondary'
              onClick={() => setBulkSetMfrOpen(false)}
              disabled={bulkRunning}
            >
              Cancel
            </Button>
            <Button
              variant='primary'
              onClick={handleBulkApply}
              loading={bulkRunning}
              disabled={!bulkTargetMfrId || manufacturersLoading || bulkRunning || bulkMfrTargets.eligibleIds.length === 0}
            >
              {bulkRunning ? 'Applying…' : 'Apply'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Bulk Set Brand modal — one derived target brand PER calibre */}
      <Modal
        isOpen={bulkSetBrandOpen}
        onClose={() => { if (!bulkRunning) setBulkSetBrandOpen(false); }}
        title={`Set brand on ${selectedCount} selected calibre${selectedCount === 1 ? '' : 's'}`}
        size='md'
      >
        <div className='space-y-4'>
          <div className='rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900'>
            <p>
              Each calibre gets the default brand of its own manufacturer, so a
              selection spanning several makers is fine.
            </p>
            <p className='mt-1'>
              Will set <code className='font-mono text-xs'>brandId</code> on{' '}
              <strong>{brandPlan?.writes.length ?? 0}</strong> calibre
              {(brandPlan?.writes.length ?? 0) === 1 ? '' : 's'}.
            </p>
            <p className='mt-1 text-blue-800'>
              An already-set brand is never overwritten.
            </p>
          </div>

          {(brandPlan?.writes.length ?? 0) > 0 && (
            <div className='max-h-48 overflow-y-auto rounded-lg border border-gray-200 divide-y divide-gray-100'>
              {brandPlan!.writes.map((w) => (
                <div key={w.id} className='flex items-center justify-between px-3 py-1.5 text-sm'>
                  <span className='text-gray-800 truncate mr-3'>{w.name}</span>
                  <Badge variant='info'>{brandLabel(w.targetBrandId)}</Badge>
                </div>
              ))}
            </div>
          )}

          {(brandPlan?.alreadySet.length ?? 0) > 0 && (
            <p className='text-sm text-gray-600'>
              <strong>{brandPlan!.alreadySet.length}</strong> calibre
              {brandPlan!.alreadySet.length === 1 ? '' : 's'} already{' '}
              {brandPlan!.alreadySet.length === 1 ? 'has' : 'have'} this brand — skipped.
            </p>
          )}

          {(brandPlan?.conflicts.length ?? 0) > 0 && (
            <div className='rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900'>
              <p>
                <strong>{brandPlan!.conflicts.length}</strong> calibre
                {brandPlan!.conflicts.length === 1 ? '' : 's'} already scoped to a
                different brand — skipped, not overwritten. Change{' '}
                {brandPlan!.conflicts.length === 1 ? 'it' : 'them'} in the editor if that's wrong.
              </p>
              <ul className='mt-1 space-y-0.5'>
                {brandPlan!.conflicts.slice(0, 8).map((c) => (
                  <li key={c.id} className='truncate'>
                    • {c.name} — {brandLabel(c.currentBrandId)} (would be{' '}
                    {brandLabel(c.targetBrandId)})
                  </li>
                ))}
                {brandPlan!.conflicts.length > 8 && (
                  <li>• …and {brandPlan!.conflicts.length - 8} more</li>
                )}
              </ul>
            </div>
          )}

          {(brandPlan?.noTarget.length ?? 0) > 0 && (
            <p className='text-sm text-amber-800'>
              <strong>{brandPlan!.noTarget.length}</strong> calibre
              {brandPlan!.noTarget.length === 1 ? '' : 's'} skipped — their manufacturer
              names no single default brand. Set one on the manufacturer (or pick a brand
              in the calibre editor).
            </p>
          )}

          {(brandPlan?.writes.length ?? 0) === 0 && (
            <p className='text-sm text-amber-800'>Nothing to set — no calibre qualifies.</p>
          )}

          <div className='flex justify-end gap-3 pt-2 border-t'>
            <Button
              variant='secondary'
              onClick={() => setBulkSetBrandOpen(false)}
              disabled={bulkRunning}
            >
              Cancel
            </Button>
            <Button
              variant='primary'
              onClick={handleBulkApplyBrand}
              loading={bulkRunning}
              disabled={bulkRunning || (brandPlan?.writes.length ?? 0) === 0}
            >
              {bulkRunning ? 'Applying…' : 'Apply'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Bulk Set AKA Brands modal — fill the brand scope of unbranded aliases */}
      <Modal
        isOpen={bulkAkaBrandsOpen}
        onClose={() => { if (!bulkRunning) setBulkAkaBrandsOpen(false); }}
        title={`Set AKA brands on ${selectedCount} selected calibre${selectedCount === 1 ? '' : 's'}`}
        size='md'
      >
        <div className='space-y-4'>
          <div className='rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900'>
            <p>
              Will scope <strong>{akaPlan?.aliasesUpdated ?? 0}</strong> unbranded AKA
              {(akaPlan?.aliasesUpdated ?? 0) === 1 ? '' : 's'} across{' '}
              <strong>{akaPlan?.writes.length ?? 0}</strong> calibre
              {(akaPlan?.writes.length ?? 0) === 1 ? '' : 's'} to that calibre's
              manufacturer default brand.
            </p>
            <p className='mt-1 text-blue-800'>
              An AKA that already names a brand — any brand — is left untouched.
            </p>
          </div>

          {(akaPlan?.writes.length ?? 0) > 0 && (
            <div className='max-h-48 overflow-y-auto rounded-lg border border-gray-200 divide-y divide-gray-100'>
              {akaPlan!.writes.map((w) => (
                <div key={w.id} className='flex items-center justify-between px-3 py-1.5 text-sm'>
                  <span className='text-gray-800 truncate mr-3'>
                    {w.name}{' '}
                    <span className='text-gray-500'>
                      ({w.filled} AKA{w.filled === 1 ? '' : 's'})
                    </span>
                  </span>
                  <Badge variant='info'>{brandLabel(w.targetBrandId)}</Badge>
                </div>
              ))}
            </div>
          )}

          {(akaPlan?.nothingToDo.length ?? 0) > 0 && (
            <p className='text-sm text-gray-600'>
              <strong>{akaPlan!.nothingToDo.length}</strong> calibre
              {akaPlan!.nothingToDo.length === 1 ? '' : 's'} had no unbranded AKAs to
              update.
            </p>
          )}

          {(akaPlan?.noTarget.length ?? 0) > 0 && (
            <p className='text-sm text-amber-800'>
              <strong>{akaPlan!.noTarget.length}</strong> calibre
              {akaPlan!.noTarget.length === 1 ? '' : 's'} skipped — their manufacturer
              names no single default brand.
            </p>
          )}

          {(akaPlan?.writes.length ?? 0) === 0 && (
            <p className='text-sm text-amber-800'>
              Nothing to set — every AKA on the selection is already scoped, or there are
              none.
            </p>
          )}

          <div className='flex justify-end gap-3 pt-2 border-t'>
            <Button
              variant='secondary'
              onClick={() => setBulkAkaBrandsOpen(false)}
              disabled={bulkRunning}
            >
              Cancel
            </Button>
            <Button
              variant='primary'
              onClick={handleBulkApplyAkaBrands}
              loading={bulkRunning}
              disabled={bulkRunning || (akaPlan?.writes.length ?? 0) === 0}
            >
              {bulkRunning ? 'Applying…' : 'Apply'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Bulk Set Image Source modal */}
      <Modal
        isOpen={bulkImgSrcOpen}
        onClose={() => { if (!bulkRunning) setBulkImgSrcOpen(false); }}
        title={`Set image source for ${selectedCount} selected calibre${selectedCount === 1 ? '' : 's'}`}
        size='md'
      >
        <div className='space-y-4'>
          <div>
            <label className='block text-sm font-medium text-gray-700 mb-1'>Image source</label>
            <select
              value={bulkTargetImgSrc}
              onChange={(e) => setBulkTargetImgSrc(e.target.value as CalibreImageSource | '')}
              disabled={bulkRunning}
              className='w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:bg-gray-50 disabled:text-gray-500'
            >
              <option value=''>Choose a source…</option>
              {CALIBRE_IMAGE_SOURCES.map((s) => (
                <option key={s} value={s}>
                  {CALIBRE_SOURCE_LABELS[s]} ({s})
                </option>
              ))}
            </select>
          </div>

          <div className='rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900'>
            <p>
              This sets <code className='font-mono text-xs'>source</code> on{' '}
              <strong>every</strong> image of each selected calibre. Existing per-image source values will be overwritten.
            </p>
            <p className='mt-1'>
              <strong>{selectedImageStats.withImages}</strong> of {selectionDocs.length} selected calibre
              {selectionDocs.length === 1 ? '' : 's'} {selectedImageStats.withImages === 1 ? 'has' : 'have'} images —{' '}
              <strong>{selectedImageStats.totalImages}</strong> image
              {selectedImageStats.totalImages === 1 ? '' : 's'} will be updated.
            </p>
            {selectedImageStats.withoutImages > 0 && (
              <p className='mt-1 text-amber-800'>
                {selectedImageStats.withoutImages} selected calibre
                {selectedImageStats.withoutImages === 1 ? '' : 's'} {selectedImageStats.withoutImages === 1 ? 'has' : 'have'} no
                images and will be skipped.
              </p>
            )}
          </div>

          <div className='flex justify-end gap-3 pt-2 border-t'>
            <Button
              variant='secondary'
              onClick={() => setBulkImgSrcOpen(false)}
              disabled={bulkRunning}
            >
              Cancel
            </Button>
            <Button
              variant='danger'
              onClick={handleBulkApplyImageSource}
              loading={bulkRunning}
              disabled={!bulkTargetImgSrc || bulkRunning || selectedImageStats.withImages === 0}
            >
              {bulkRunning ? 'Applying…' : 'Apply'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Bulk Set Calibre Source modal (entity-level `source`, not per-image) */}
      <Modal
        isOpen={bulkCalSrcOpen}
        onClose={() => { if (!bulkRunning) setBulkCalSrcOpen(false); }}
        title={`Set calibre source for ${selectedCount} selected calibre${selectedCount === 1 ? '' : 's'}`}
        size='md'
      >
        <div className='space-y-4'>
          <div>
            <label className='block text-sm font-medium text-gray-700 mb-1'>Calibre source</label>
            <select
              value={bulkTargetCalSrc}
              onChange={(e) => setBulkTargetCalSrc(e.target.value as CalibreEntitySource | '')}
              disabled={bulkRunning}
              className='w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:bg-gray-50 disabled:text-gray-500'
            >
              <option value=''>Choose a source…</option>
              {CALIBRE_ENTITY_SOURCES.map((s) => (
                <option key={s} value={s}>
                  {CALIBRE_SOURCE_LABELS[s]} ({s})
                </option>
              ))}
            </select>
          </div>

          <div className='rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900'>
            <p>
              This sets the calibre-level <code className='font-mono text-xs'>source</code> on{' '}
              <strong>{selectedCount}</strong> selected calibre{selectedCount === 1 ? '' : 's'}. The existing
              calibre-level source value will be overwritten.
            </p>
          </div>

          <div className='flex justify-end gap-3 pt-2 border-t'>
            <Button
              variant='secondary'
              onClick={() => setBulkCalSrcOpen(false)}
              disabled={bulkRunning}
            >
              Cancel
            </Button>
            <Button
              variant='danger'
              onClick={handleBulkApplyCalibreSource}
              loading={bulkRunning}
              disabled={!bulkTargetCalSrc || bulkRunning}
            >
              {bulkRunning ? 'Applying…' : 'Apply'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Bulk AI Calibre Enhancement modal — review→apply over the selection.
          Conditionally mounted so each open starts from a clean run/review state.
          Fed hydrated DOCUMENTS, never listing rows: the tool reads and writes
          back `data.*`, none of which is on an Algolia record. */}
      {aiEnhanceOpen && (
        <BulkCalibreAIEnhance
          isOpen
          onClose={() => setAiEnhanceOpen(false)}
          calibres={selectionDocs}
          onApplied={async () => {
            await loadRows();
          }}
        />
      )}

      {/* Backfill AI tiers modal */}
      <Modal
        isOpen={tiersBackfillOpen}
        onClose={() => { if (!tiersBackfillRunning) setTiersBackfillOpen(false); }}
        title={
          tiersBackfillScope === 'selection'
            ? `Backfill AI movement tiers for ${tiersBackfillIds.length} selected calibre${tiersBackfillIds.length === 1 ? '' : 's'}`
            : 'Backfill AI movement tiers'
        }
        size='lg'
      >
        <div className='space-y-4'>
          <div className='rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900 space-y-1'>
            <p>
              Re-runs AI enrichment on{' '}
              {tiersBackfillScope === 'selection'
                ? 'the selected calibres'
                : 'calibres whose tiers are empty'}{' '}
              and <strong>auto-accepts</strong> any{' '}
              <code className='font-mono text-xs'>tiers</code> the AI returns.
              {tiersBackfillScope === 'selection'
                ? ' Calibres that already have tiers are skipped unless force is on.'
                : ' Calibres that already have tiers are never touched (fill-empty only).'}
            </p>
            <p className='text-blue-800'>
              Each calibre is one AI call, so a real run works in batches and may take
              a while.
              {tiersBackfillScope === 'selection'
                ? ' A calibre an earlier run already attempted is still re-tried here — picking rows is a deliberate re-ask.'
                : ' Start with a dry run to see how many are eligible.'}
            </p>
          </div>

          {tiersBackfillScope === 'selection' ? (
            <div className='space-y-3'>
              {tiersBackfillPreviewing && (
                <p className='text-sm text-gray-600'>Checking the selected calibres…</p>
              )}

              {tiersBackfillPreviewError && (
                <div className='rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800'>
                  {tiersBackfillPreviewError}
                </div>
              )}

              {tiersBackfillPreview && !tiersBackfillPreviewing && (
                <div className='rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-800 space-y-1'>
                  <p>
                    <strong>{tiersBackfillPreview.ids.length}</strong> selected ·{' '}
                    <strong>{tiersBackfillPreview.candidates}</strong> will be enriched ·{' '}
                    <strong>{tiersBackfillPreview.skippedWithTiers}</strong> skipped (already
                    have tiers)
                    {tiersBackfillPreview.notFound > 0 && (
                      <> · <strong>{tiersBackfillPreview.notFound}</strong> not found</>
                    )}
                  </p>
                  {tiersBackfillPreview.attemptedBefore > 0 && (
                    <p className='text-gray-600'>
                      {tiersBackfillPreview.attemptedBefore} of these were attempted by an
                      earlier backfill run and will be re-tried.
                    </p>
                  )}
                  {tiersBackfillPreview.overwrites > 0 && (
                    <p className='text-amber-800'>
                      {tiersBackfillPreview.overwrites} already{' '}
                      {tiersBackfillPreview.overwrites === 1 ? 'has' : 'have'} tiers and will be{' '}
                      <strong>overwritten</strong>:{' '}
                      {tiersBackfillPreview.overwriteNames.slice(0, 10).join(', ')}
                      {tiersBackfillPreview.overwriteNames.length > 10 && ', …'}
                    </p>
                  )}
                  {tiersBackfillPreview.candidateNames.length > 0 && (
                    <p className='text-gray-600'>
                      {tiersBackfillPreview.candidateNames.slice(0, 15).join(', ')}
                      {tiersBackfillPreview.candidateNames.length > 15 &&
                        ` …and ${tiersBackfillPreview.candidateNames.length - 15} more`}
                    </p>
                  )}
                </div>
              )}

              <label className='flex items-start gap-2 text-sm text-gray-700'>
                <input
                  type='checkbox'
                  checked={tiersBackfillForce}
                  onChange={(e) => toggleTiersBackfillForce(e.target.checked)}
                  disabled={tiersBackfillRunning || tiersBackfillPreviewing}
                  className='mt-0.5 rounded text-brand-600 focus:ring-brand-500'
                />
                <span>
                  Force — also re-enrich calibres that already have tiers.{' '}
                  <span className='text-amber-800'>
                    This overwrites their stored tiers with whatever the AI returns.
                  </span>
                </span>
              </label>
            </div>
          ) : (
            <label className='flex items-center gap-2 text-sm text-gray-700'>
              <input
                type='checkbox'
                checked={tiersBackfillDryRun}
                onChange={(e) => setTiersBackfillDryRun(e.target.checked)}
                disabled={tiersBackfillRunning}
                className='rounded text-brand-600 focus:ring-brand-500'
              />
              Dry run (scan &amp; count only — no AI calls, no writes)
            </label>
          )}

          {tiersBackfillLog.length > 0 && (
            <div className='max-h-64 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 font-mono text-xs text-gray-800 whitespace-pre-wrap'>
              {tiersBackfillLog.map((line, i) => (
                <div key={i}>{line}</div>
              ))}
            </div>
          )}

          <div className='flex justify-end gap-3 pt-2 border-t'>
            <Button
              variant='secondary'
              onClick={() => setTiersBackfillOpen(false)}
              disabled={tiersBackfillRunning}
            >
              Close
            </Button>
            <Button
              variant='primary'
              onClick={runTiersBackfill}
              loading={tiersBackfillRunning}
              disabled={
                tiersBackfillRunning ||
                (tiersBackfillScope === 'selection' &&
                  (tiersBackfillPreviewing || (tiersBackfillPreview?.candidates ?? 0) === 0))
              }
            >
              {tiersBackfillRunning
                ? 'Running…'
                : tiersBackfillScope === 'selection'
                  ? `Enrich ${tiersBackfillPreview?.candidates ?? 0} calibre${(tiersBackfillPreview?.candidates ?? 0) === 1 ? '' : 's'}`
                  : tiersBackfillDryRun
                    ? 'Run dry run'
                    : 'Run backfill'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Repair deprecated calibres modal */}
      <Modal
        isOpen={hygieneOpen}
        onClose={() => { if (!hygieneRunning) setHygieneOpen(false); }}
        title='Repair deprecated calibres'
        size='lg'
      >
        <div className='space-y-4'>
          <div className='rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900 space-y-1'>
            <p>
              Calibres reclassified into a tier before the wizard moved a whole
              alias set still carry their{' '}
              <code className='font-mono text-xs'>aliasDetails</code> rows — on a
              soft-deleted document nothing can match any more. This moves them
              onto the redirect target, snapshots and clears them from the source,
              and renames the source{' '}
              <code className='font-mono text-xs'>… (deprecated)</code>.
            </p>
            <p className='text-blue-800'>
              Idempotent: an already-repaired calibre is skipped, so this is safe
              to run more than once. Start with a dry run.
            </p>
          </div>

          <label className='flex items-center gap-2 text-sm text-gray-700'>
            <input
              type='checkbox'
              checked={hygieneDryRun}
              onChange={(e) => setHygieneDryRun(e.target.checked)}
              disabled={hygieneRunning}
              className='rounded text-brand-600 focus:ring-brand-500'
            />
            Dry run (report only — no writes)
          </label>

          {hygieneLog.length > 0 && (
            <div className='max-h-64 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 font-mono text-xs text-gray-800 whitespace-pre-wrap'>
              {hygieneLog.map((line, i) => (
                <div key={i}>{line}</div>
              ))}
            </div>
          )}

          <div className='flex justify-end gap-3 pt-2 border-t'>
            <Button
              variant='secondary'
              onClick={() => setHygieneOpen(false)}
              disabled={hygieneRunning}
            >
              Close
            </Button>
            <Button
              variant='primary'
              onClick={runHygiene}
              loading={hygieneRunning}
              disabled={hygieneRunning}
            >
              {hygieneRunning ? 'Running…' : hygieneDryRun ? 'Run dry run' : 'Run repair'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Edit/Create modal */}
      <CustomCalibreModal
        isOpen={modalOpen}
        onClose={handleModalClose}
        initialEditEntry={modalEntry}
        initialMode={modalCreate ? 'create' : 'list'}
        onSaved={handleSaved}
      />
    </div>
  );
}

interface CalibreRowProps {
  row: CalibreListRow;
  onEdit: (row: CalibreListRow) => void | Promise<void>;
  onDelete: (row: CalibreListRow) => void;
  deleting: boolean;
  selected: boolean;
  onToggleSelect: (id: string) => void;
  manufacturerNameById: Map<string, string>;
  /** The Manufacturer filter in force — drives the "now X" / "originally X" capacity badge. */
  browsingManufacturerId?: string;
}

function CalibreRow({ row, onEdit, onDelete, deleting, selected, onToggleSelect, manufacturerNameById, browsingManufacturerId }: CalibreRowProps) {
  const badge = browsingManufacturerId ? originalMakerBadge(row, browsingManufacturerId) : null;
  const aliases = (row.alsoKnownAs || []).join(', ');
  const created = row.createdAt?.toDate?.();
  const refsCell = describeCalibreRefs(row);
  const sourceVariant: 'info' | 'default' = row.source === 'calibercorner' ? 'info' : 'default';
  const sourceLabel = row.source === 'calibercorner' ? 'CalibreCorner' : row.source === 'manual' ? 'Manual' : (row.source || '—');

  return (
    <tr
      className={`hover:bg-gray-50 cursor-pointer ${selected ? 'bg-brand-50/40' : ''} ${row.deprecated ? 'opacity-60' : ''}`}
      onClick={() => void onEdit(row)}
    >
      <td className='px-3 py-3' onClick={(e) => e.stopPropagation()}>
        <input
          type='checkbox'
          checked={selected}
          onChange={() => onToggleSelect(row.id)}
          aria-label={`Select ${row.name}`}
          className='rounded text-brand-600 focus:ring-brand-500'
        />
      </td>
      <td className='px-4 py-3'>
        <div className='text-sm font-medium text-gray-900'>
          {row.name}
          {row.deprecated && (
            <span
              className='ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide bg-gray-200 text-gray-600'
              title='Redirect alias — reclassified as a tier of another calibre'
            >
              Deprecated
            </span>
          )}
        </div>
        {row.movementType && (
          <div className='text-xs text-gray-500 capitalize'>{row.movementType}</div>
        )}
      </td>
      <td className='px-3 py-3 text-sm text-gray-600'>
        {(row.manufacturerId && manufacturerNameById.get(row.manufacturerId)) || <span className='text-gray-400'>—</span>}
        {row.additionalManufacturerIds && row.additionalManufacturerIds.length > 0 && (
          <div className='text-xs text-gray-400'>
            co-developed with{' '}
            {row.additionalManufacturerIds
              .map((id) => manufacturerNameById.get(id) || id)
              .join(', ')}
          </div>
        )}
        {badge && (
          <span className='mt-0.5 inline-block rounded bg-amber-50 px-1.5 py-0.5 text-[11px] text-amber-800 border border-amber-200'>
            {badge.kind} {manufacturerNameById.get(badge.manufacturerId) || badge.manufacturerId}
          </span>
        )}
      </td>
      <td className='px-4 py-3 text-xs text-gray-500'>
        {aliases ? (
          <span className='block max-w-xs truncate' title={aliases}>{aliases}</span>
        ) : (
          <span className='text-gray-400'>—</span>
        )}
      </td>
      <td className='px-3 py-3'>
        <Badge variant={sourceVariant}>{sourceLabel}</Badge>
      </td>
      <td className='px-3 py-3 text-center text-sm text-gray-600'>{row.jewels ?? '—'}</td>
      <td className='px-3 py-3 text-center text-sm text-gray-600'>{row.powerReserveHours ? `${row.powerReserveHours}h` : '—'}</td>
      <td className='px-3 py-3 text-center text-sm text-gray-600 whitespace-nowrap'>
        {refsCell.count || '—'}
        {refsCell.tooltip && (
          <span
            className='ml-1.5 inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[11px] font-medium text-amber-800'
            title={refsCell.tooltip}
          >
            ⚠️ {refsCell.unlinked} unlinked
          </span>
        )}
      </td>
      <td className='px-3 py-3 text-center'><CalibreThumbnail imageUrl={row.imageUrl} image={row.image} /></td>
      <td className='px-4 py-3 text-xs text-gray-500'>
        {created ? created.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
      </td>
      <td className='px-4 py-3 text-right'>
        <div className='flex justify-end gap-2' onClick={(e) => e.stopPropagation()}>
          <Button variant='ghost' size='sm' onClick={() => void onEdit(row)}>Edit</Button>
          <Button variant='ghost' size='sm' onClick={() => onDelete(row)} loading={deleting} className='text-red-600 hover:text-red-700'>
            Delete
          </Button>
        </div>
      </td>
    </tr>
  );
}

function GroupedRows({ brandKey, rows, onEdit, onDelete, deletingId, selected, onToggleSelect, manufacturerNameById, browsingManufacturerId }: {
  brandKey: string;
  rows: CalibreListRow[];
  onEdit: (row: CalibreListRow) => void | Promise<void>;
  onDelete: (row: CalibreListRow) => void;
  deletingId: string | null;
  selected: Set<string>;
  onToggleSelect: (id: string) => void;
  manufacturerNameById: Map<string, string>;
  browsingManufacturerId?: string;
}) {
  return (
    <>
      <tr className='bg-gray-100'>
        <td colSpan={11} className='px-4 py-2 text-xs font-semibold text-gray-700 uppercase tracking-wide'>
          {brandKey} <span className='ml-2 text-gray-500 font-normal'>({rows.length})</span>
        </td>
      </tr>
      {rows.map((row) => (
        <CalibreRow
          key={row.id}
          row={row}
          onEdit={onEdit}
          onDelete={onDelete}
          deleting={deletingId === row.id}
          selected={selected.has(row.id)}
          onToggleSelect={onToggleSelect}
          manufacturerNameById={manufacturerNameById}
          browsingManufacturerId={browsingManufacturerId}
        />
      ))}
    </>
  );
}
